import React from 'react';
import { Button, ListGroup, Modal, StatusBadge } from '@/components/ui';
import { formatDateTime } from '@/utils/displayDateTime';

interface UnapprovedSubmission {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  taskFieldValue: string | null;
  statusRequestedBy: {
    id: number;
    name: string;
    lastName: string;
    email: string;
  } | null;
}

interface UnapprovedSubmissionsWarningProps {
  isVisible: boolean;
  submissionCount: number;
  submissions: UnapprovedSubmission[];
  onConfirm: () => void;
  onCancel: () => void;
  actionType?: 'complete' | 'set-internal-review';
}

const UnapprovedSubmissionsWarning: React.FC<UnapprovedSubmissionsWarningProps> = ({
  isVisible,
  submissionCount,
  submissions,
  onConfirm,
  onCancel,
  actionType = 'complete'
}) => {
  const getWarningMessage = () => {
    const baseMessage = `There ${submissionCount === 1 ? 'is' : 'are'} ${submissionCount} unapproved submission${submissionCount !== 1 ? 's' : ''} for this task.`;
    
    if (actionType === 'complete') {
      return `${baseMessage} Are you sure you want to mark the task as complete without these costs?`;
    } else {
      return `${baseMessage} Are you sure you want to close the task without these costs?`;
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString) || dateString;
  };

  return (
    // A warning/confirm: content-sized (autoHeight) rather than the standard fixed height.
    <Modal show={isVisible} onHide={onCancel} size="lg" autoHeight>
      <Modal.Header className="!tw-bg-warning tw-text-black">
        <Modal.Title>
          <i className="bi bi-exclamation-triangle tw-mr-2"></i>
          Unapproved Submissions Warning
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="tw-mb-3">
          {getWarningMessage()}
        </p>

        {/* List of submissions */}
        {submissions.length > 0 && (
          <div>
            <h6 className="tw-mb-3">Pending Form Submissions:</h6>
            <ListGroup>
              {submissions.map((submission) => (
                <ListGroup.Item key={submission.id} className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-ml-2 tw-mr-auto">
                    <div className="tw-font-bold">{submission.name}</div>
                    <small className="tw-text-content-muted">
                      Submitted: {formatDate(submission.createdAt)}
                      {submission.statusRequestedBy && (
                        <span className="tw-ml-2">
                          by {submission.statusRequestedBy.name} {submission.statusRequestedBy.lastName}
                        </span>
                      )}
                    </small>
                  </div>
                  {/* Was a local getStatusBadgeVariant map (its own colour for "active"/"pending"/
                      "need revisions") - the same drift QA flagged on the task table. StatusBadge
                      is the single source of truth; unrecognized submission statuses (this vocab
                      isn't part of the task lifecycle) fall back to its default grey. */}
                  <StatusBadge status={submission.status} pill />
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="warning"
          onClick={onConfirm}
        >
          Continue Anyway
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnapprovedSubmissionsWarning; 